import { Dispatch, ActionCreator } from "redux";
import { ThunkAction } from "redux-thunk";

import { configUrl, maxServiceProviderCount } from "../constants/Config";
import { ApplicationState } from "../redux/ApplicationState";
import {
  GetDischargeReason,
  GetDisciplineList,
  GetFeatureAccessData,
  GetLanguageMaster,
  GetMasterCareType,
  GetMasterDocumentNotes,
  GetMasterDocumentType,
  GetMasterHealthPlan,
  GetMasterReferralSource,
  GetMasterRole,
  GetMasterSendCommunicationAuth,
  GetMasterServiceProvider,
  GetMasterServiceProviderDefault,
  GetNotificationType,
  GetNPPESDetailsData,
} from "../services/Master";
import { MasterRoleDispatchTypes } from "../models/dispatchTypes/User";
import { MasterRoleActionDispatchTypes } from "../constants/User";
import { MasterServiceProviderDefaultDispatchTypes, MasterServiceProviderDispatchTypes } from "../models/dispatchTypes/ServiceProvider";
import { MasterServiceProviderActionDispatchTypes, MasterServiceProviderDefaultActionDispatchTypes } from "../constants/ServiceProvider";
import {
  LanguageDispatchTypes,
  MasterCareTypeDispatchTypes,
  MasterDischargeReasonDispatchTypes,
  MasterDisciplineDispatchTypes,
  MasterDocumentNoteDispatchTypes,
  MasterDocumentTypeDispatchTypes,
  MasterNotificationTypeDispatchTypes,
  MasterPayersDispatchTypes,
  MasterReferralSourceDispatchTypes,
  MasterSendCommunicationAuthTypeDispatchTypes,
} from "../models/dispatchTypes/Master";
import {
  LanguageMasterActionDispatchTypes,
  MasterCareTypeActionDispatchTypes,
  MasterDischargeReasonActionDispatchTypes,
  MasterDisciplineActionDispatchTypes,
  MasterDocumentNoteActionDispatchTypes,
  MasterDocumentTypeActionDispatchTypes,
  MasterFeatureAccessActionDispatchTypes,
  MasterNotificationTypeActionDispatchTypes,
  MasterPayerActionDispatchTypes,
  MasterReferralSourceActionDispatchTypes,
  MasterSendCommunicationAuthTypeActionDispatchTypes,
} from "../constants/Master";
import { AgencyListPayload } from "../models/ProviderMaterials";
import { ReferralIntakePhysicianDetailsDispatchTypes } from "../models/dispatchTypes/PhysicianDetails";
import { GetNPPESDataActionDispatchTypes } from "../constants/PhysicianDetails";

export const getMasterRole: ActionCreator<
  ThunkAction<
    void,
    ApplicationState,
    Record<string, never>,
    MasterRoleDispatchTypes
  >
> = () => {
  return async (dispatch: Dispatch) => {
    dispatch({
      type: MasterRoleActionDispatchTypes.ROLE_LOADING,
    });
    const response = await GetMasterRole(configUrl);
    if (response && response.data) {
      dispatch({
        response: response.data,
        type: MasterRoleActionDispatchTypes.ROLE_SUCCESS,
      });
    } else {
      dispatch({
        type: MasterRoleActionDispatchTypes.ROLE_ERROR,
      });
    }
  };
};

export const getMasterServiceProvider: ActionCreator<
  ThunkAction<
    void,
    ApplicationState,
    Record<string, never>,
    MasterServiceProviderDispatchTypes
  >
> = (payload?: AgencyListPayload) => {
  return async (dispatch: Dispatch) => {
    dispatch({
      type: MasterServiceProviderActionDispatchTypes.MASTER_SERVICE_PROVIDER_INVOKE,
    });
    const response = await GetMasterServiceProvider(configUrl, maxServiceProviderCount, payload);
    if (response && response.status) {
      switch (response.status) {
        case 422:
        case 400:
        case 404:
        case 500:
          dispatch({
            error: response,
            type: MasterServiceProviderActionDispatchTypes.MASTER_SERVICE_PROVIDER_ERROR,
          });
          break;
        case 200:
          dispatch({
            response: response.data?.serviceProviders,
            type: MasterServiceProviderActionDispatchTypes.MASTER_SERVICE_PROVIDER_SUCCESS,
          });
          break;
        default:
          break;
      }
    }
  };
};

export const getMasterServiceProviderDefault: ActionCreator<
  ThunkAction<
    void,
    ApplicationState,
    Record<string, never>,
    MasterServiceProviderDefaultDispatchTypes
  >
> = () => {
  return async (dispatch: Dispatch) => {
    dispatch({
      type: MasterServiceProviderDefaultActionDispatchTypes.MASTER_SERVICE_PROVIDER_DEFAULT_INVOKE,
    });
    const response = await GetMasterServiceProviderDefault(configUrl, maxServiceProviderCount);
    if (response && response.status) {
      switch (response.status) {
        case 422:
        case 400:
        case 404:
        case 500:
          dispatch({
            error: response,
            type: MasterServiceProviderDefaultActionDispatchTypes.MASTER_SERVICE_PROVIDER_DEFAULT_ERROR,
          });
          break;
        case 200:
          dispatch({
            response: response.data?.serviceProviders,
            type: MasterServiceProviderDefaultActionDispatchTypes.MASTER_SERVICE_PROVIDER_DEFAULT_SUCCESS,
          });
          break;
        default:
          break;
      }
    }
  }
}

export const getMasterHealthPlan: ActionCreator<
  ThunkAction<
    void,
    ApplicationState,
    Record<string, never>,
    MasterPayersDispatchTypes
  >
> = () => {
  return async (dispatch: Dispatch) => {
    dispatch({
      type: MasterPayerActionDispatchTypes.MASTER_PAYER_INVOKE,
    });
    const response = await GetMasterHealthPlan(configUrl);
    if (response && response.status) {
      switch (response.status) {
        case 422:
        case 400:
        case 404:
        case 500:
          dispatch({
            type: MasterPayerActionDispatchTypes.MASTER_PAYER_ERROR,
          });
          break;
        case 200:
          dispatch({
            response: response.data,
            type: MasterPayerActionDispatchTypes.MASTER_PAYER_SUCCESS,
          });
          break;
        default:
          break;
      }
    }
  };
};

export const getCareTypes: ActionCreator<
  ThunkAction<
    void,
    ApplicationState,
    Record<string, never>,
    MasterCareTypeDispatchTypes
  >
> = () => {
  return async (dispatch: Dispatch) => {
    dispatch({
      type: MasterCareTypeActionDispatchTypes.MASTER_CARE_TYPE_INVOKE,
    });
    const response = await GetMasterCareType(configUrl);
    if (response && response.status) {
      switch (response.status) {
        case 422:
        case 400:
        case 404:
        case 500:
          dispatch({
            type: MasterCareTypeActionDispatchTypes.MASTER_CARE_TYPE_ERRORS,
          });
          break;
        case 200:
          dispatch({
            response: response.data,
            type: MasterCareTypeActionDispatchTypes.MASTER_CARE_TYPE_SUCCESS,
          });
          break;
        default:
          break;
      }
    }
  };
};

export const getSendCommunicationAuthType: ActionCreator<
  ThunkAction<
    void,
    ApplicationState,
    Record<string, never>,
    MasterSendCommunicationAuthTypeDispatchTypes
  >
> = () => {
  return async (dispatch: Dispatch) => {
    dispatch({
      type: MasterSendCommunicationAuthTypeActionDispatchTypes.MASTER_SEND_COMMUNICATION_AUTH_TYPE_INVOKE,
    });
    const response = await GetMasterSendCommunicationAuth(configUrl);
    if (response && response.status) {
      switch (response.status) {
        case 422:
        case 400:
        case 404:
        case 500:
          dispatch({
            type: MasterSendCommunicationAuthTypeActionDispatchTypes.MASTER_SEND_COMMUNICATION_AUTH_TYPE_ERRORS,
          });
          break;
        case 200:
          dispatch({
            response: response.data,
            type: MasterSendCommunicationAuthTypeActionDispatchTypes.MASTER_SEND_COMMUNICATION_AUTH_TYPE_SUCCESS,
          });
          break;
        default:
          break;
      }
    }
  };
};

export const getMasterReferralSource: ActionCreator<
  ThunkAction<
    void,
    ApplicationState,
    Record<string, never>,
    MasterReferralSourceDispatchTypes
  >
> = () => {
  return async (dispatch: Dispatch) => {
    dispatch({
      type: MasterReferralSourceActionDispatchTypes.MASTER_REFERRAL_SOURCE_INVOKE,
    });
    const response = await GetMasterReferralSource(configUrl);
    if (response && response.status) {
      switch (response.status) {
        case 422:
        case 400:
        case 404:
        case 500:
          dispatch({
            type: MasterReferralSourceActionDispatchTypes.MASTER_REFERRAL_SOURCE_ERRORS,
          });
          break;
        case 200:
          dispatch({
            response: response.data,
            type: MasterReferralSourceActionDispatchTypes.MASTER_REFERRAL_SOURCE_SUCCESS,
          });
          break;
        default:
          break;
      }
    }
  };
};

export const getMasterDocumentType: ActionCreator<
  ThunkAction<
    void,
    ApplicationState,
    Record<string, never>,
    MasterDocumentTypeDispatchTypes
  >
> = () => {
  return async (dispatch: Dispatch) => {
    dispatch({
      type: MasterDocumentTypeActionDispatchTypes.MASTER_DOCUMENT_TYPE_INVOKE,
    });
    const response = await GetMasterDocumentType(configUrl);
    if (response && response.status) {
      switch (response.status) {
        case 422:
        case 400:
        case 404:
        case 500:
          dispatch({
            response,
            type: MasterDocumentTypeActionDispatchTypes.MASTER_DOCUMENT_TYPE_ERRORS,
          });
          break;
        case 200:
          dispatch({
            response: response.data,
            type: MasterDocumentTypeActionDispatchTypes.MASTER_DOCUMENT_TYPE_SUCCESS,
          });
          break;
        default:
          break;
      }
    }
  };
};

export const getMasterDocumentNotes: ActionCreator<
  ThunkAction<
    void,
    ApplicationState,
    Record<string, never>,
    MasterDocumentNoteDispatchTypes
  >
> = () => {
  return async (dispatch: Dispatch) => {
    dispatch({
      type: MasterDocumentNoteActionDispatchTypes.MASTER_DOCUMENT_NOTE_INVOKE,
    });
    const response = await GetMasterDocumentNotes(configUrl);
    if (response && response.status) {
      switch (response.status) {
        case 422:
        case 400:
        case 404:
        case 500:
          dispatch({
            response,
            type: MasterDocumentNoteActionDispatchTypes.MASTER_DOCUMENT_NOTE_ERRORS,
          });
          break;
        case 200:
          dispatch({
            response: response.data,
            type: MasterDocumentNoteActionDispatchTypes.MASTER_DOCUMENT_NOTE_SUCCESS,
          });
          break;
        default:
          break;
      }
    }
  };
};

export const getDisciplineList: ActionCreator<
  ThunkAction<
    void,
    ApplicationState,
    Record<string, never>,
    MasterDisciplineDispatchTypes
  >
> = (eligibilityId: number, isPortal: boolean, type?: number) => {
  return async (dispatch: Dispatch) => {
    dispatch({
      type: MasterDisciplineActionDispatchTypes.MASTER_DISCIPLINE_INVOKE,
    });
    const response = await GetDisciplineList(configUrl, eligibilityId, isPortal, type);
    if (response && response.status) {
      switch (response.status) {
        case 422:
        case 400:
        case 404:
        case 500:
          dispatch({
            type: MasterDisciplineActionDispatchTypes.MASTER_DISCIPLINE_ERRORS,
          });
          break;
        case 200:
          dispatch({
            response: response.data,
            type: MasterDisciplineActionDispatchTypes.MASTER_DISCIPLINE_SUCCESS,
          });
          break;
        default:
          break;
      }
    }
  };
  };

  export const getFeatureAccessData: ActionCreator<
  ThunkAction<
    void,
    ApplicationState,
    Record<string, never>,
    MasterPayersDispatchTypes
  >
> = () => {
  return async (dispatch: Dispatch) => {
    dispatch({
      type: MasterFeatureAccessActionDispatchTypes.MASTER_FEATURE_ACCESS_INVOKE,
    });
    const response = await GetFeatureAccessData(configUrl);
    dispatch({
      response: response.data,
      type: MasterFeatureAccessActionDispatchTypes.MASTER_FEATURE_ACCESS_SUCCESS,
    });
  };
  };

  export const getNPPEsDetailsData: ActionCreator<
  ThunkAction<
    void,
    ApplicationState,
    Record<string, never>,
    ReferralIntakePhysicianDetailsDispatchTypes
  >
> = (payload: any) => {
  return async (dispatch: Dispatch) => {
    dispatch({
      type: GetNPPESDataActionDispatchTypes.GET_NPPES_DATA_INVOKE,
    });

    const response = await GetNPPESDetailsData(
      configUrl,
      payload
    );
     if (response && response.status) {
      switch (response.status) {
        case 422:
        case 400:
        case 404:
        case 500:
          dispatch({
            type: GetNPPESDataActionDispatchTypes.GET_NPPES_DATA_RESET,
          });
          break;
        case 200:
          dispatch({
            response: response.data,
            type: GetNPPESDataActionDispatchTypes.GET_NPPES_DATA_SUCCESS,
          });
          break;
        default:
          break;
      }
    }
  };
  };

   export const getDischargeReason: ActionCreator<
  ThunkAction<
    void,
    ApplicationState,
    Record<string, never>,
    MasterDischargeReasonDispatchTypes
  >
> = () => {
  return async (dispatch: Dispatch) => {
    dispatch({
      type: MasterDischargeReasonActionDispatchTypes.MASTER_DISCHARGE_REASON_INVOKE,
    });

    const response = await GetDischargeReason(
      configUrl
    );
     if (response && response.status) {
      switch (response.status) {
        case 422:
        case 400:
        case 404:
        case 500:
          dispatch({
            type: MasterDischargeReasonActionDispatchTypes.MASTER_DISCHARGE_REASON_ERRORS,
          });
          break;
        case 200:
          dispatch({
            response: response.data,
            type: MasterDischargeReasonActionDispatchTypes.MASTER_DISCHARGE_REASON_SUCCESS,
          });
          break;
        default:
          break;
      }
    }
  };
  };


   export const getNotificationTypeList: ActionCreator<
  ThunkAction<
    void,
    ApplicationState,
    Record<string, never>,
    MasterNotificationTypeDispatchTypes
  >
> = () => {
  return async (dispatch: Dispatch) => {
    dispatch({
      type: MasterNotificationTypeActionDispatchTypes.MASTER_NOTIFICATION_TYPE_INVOKE,
    });

    const response = await GetNotificationType(
      configUrl
    );
     if (response && response.status) {
      switch (response.status) {
        case 422:
        case 400:
        case 404:
        case 500:
          dispatch({
            type: MasterNotificationTypeActionDispatchTypes.MASTER_NOTIFICATION_TYPE_ERROR,
          });
          break;
        case 200:
          dispatch({
            response: response.data,
            type: MasterNotificationTypeActionDispatchTypes.MASTER_NOTIFICATION_TYPE_SUCCESS,
          });
          break;
        default:
          break;
      }
    }
  };
  };

export const getLanguageMasterResp: ActionCreator<
  ThunkAction<
    void,
    ApplicationState,
    Record<string, never>,
    LanguageDispatchTypes
  >
> = () => {
  return async (dispatch: Dispatch) => {
    dispatch({
      type: LanguageMasterActionDispatchTypes.LANGUAGE_MASTER_INVOKE,
    });

    const response = await GetLanguageMaster(configUrl);

    if (response && response.status) {
      switch (response.status) {
        case 422:
        case 400:
        case 404:
        case 500:
          dispatch({
            type: LanguageMasterActionDispatchTypes.LANGUAGE_MASTER_ERROR,
            error: response.data,
          });
          break;
        case 200:
          dispatch({
            response: response.data,
            type: LanguageMasterActionDispatchTypes.LANGUAGE_MASTER_SUCCESS,
          });
          break;
        default:
          break;
      }
    }
  };
};